import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingButtons from '../../components/feature/FloatingButtons';
import AgendamentoModal from '../../components/feature/AgendamentoModal';
import AnimatedSection from '../../components/base/AnimatedSection';

// ─── Tipos de conteúdo ────────────────────────────────────────────────────────

interface PolicySection {
  id: string;
  icon: string;
  title: string;
  content: string[];
  list?: string[];
}

// ─── Dados da Política ────────────────────────────────────────────────────────

const LAST_UPDATED = '19 de março de 2025';

const POLICY_SECTIONS: PolicySection[] = [
  {
    id: 'controlador',
    icon: 'ri-user-settings-line',
    title: '1. Controlador dos Dados',
    content: [
      'O controlador responsável pelo tratamento dos seus dados pessoais é:',
      'Dra. Laura Folchini — CRM/SC 21.308 · RQE 26.072',
      'Para exercer seus direitos ou esclarecer dúvidas, entre em contato pelo e-mail contato@dralaurafolchini.com.br ou pelo WhatsApp (48) 99100-2443.',
    ],
  },
  {
    id: 'dados-coletados',
    icon: 'ri-file-list-3-line',
    title: '2. Dados Pessoais Coletados',
    content: [
      'Coletamos apenas os dados estritamente necessários para prestar o atendimento solicitado. Os dados podem ser fornecidos por você ao preencher o formulário de contato disponível neste site:',
    ],
    list: [
      'Nome completo',
      'Endereço de e-mail',
      'Telefone / WhatsApp',
      'Assunto e mensagem enviados no formulário de contato',
    ],
  },
  {
    id: 'finalidade',
    icon: 'ri-focus-line',
    title: '3. Finalidade do Tratamento',
    content: [
      'Os dados pessoais coletados são utilizados exclusivamente para as seguintes finalidades:',
    ],
    list: [
      'Responder às dúvidas e solicitações enviadas pelo formulário de contato',
      'Entrar em contato para agendamento de consultas e procedimentos',
      'Enviar informações pertinentes à consulta ou ao tratamento solicitado',
      'Cumprir obrigações legais e regulatórias aplicáveis',
    ],
  },
  {
    id: 'base-legal',
    icon: 'ri-scales-line',
    title: '4. Base Legal',
    content: [
      'O tratamento dos dados pessoais é realizado com fundamento nas seguintes hipóteses legais previstas na Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018):',
    ],
    list: [
      'Consentimento do titular (art. 7º, I da LGPD): ao preencher e enviar o formulário de contato, você consente com o uso dos seus dados para as finalidades descritas',
      'Legítimo interesse do controlador (art. 7º, IX da LGPD): para responder e dar continuidade ao atendimento solicitado',
      'Cumprimento de obrigação legal ou regulatória (art. 7º, II da LGPD): quando aplicável',
    ],
  },
  {
    id: 'compartilhamento',
    icon: 'ri-share-line',
    title: '5. Compartilhamento de Dados',
    content: [
      'Seus dados pessoais não são vendidos, alugados ou compartilhados com terceiros para fins comerciais.',
      'Eventualmente, podem ser compartilhados apenas com:',
    ],
    list: [
      'Prestadores de serviços tecnológicos que operam a plataforma do site (processadores de dados), vinculados por obrigações de confidencialidade',
      'Autoridades públicas ou órgãos reguladores quando exigido por lei ou ordem judicial',
    ],
  },
  {
    id: 'retencao',
    icon: 'ri-time-line',
    title: '6. Retenção dos Dados',
    content: [
      'Os dados pessoais coletados pelo formulário de contato são mantidos pelo tempo necessário para atender à finalidade que motivou a coleta e para cumprimento de obrigações legais.',
      'Após o encerramento da finalidade, os dados são eliminados de forma segura, salvo quando a lei exigir sua manutenção por prazo determinado.',
    ],
  },
  {
    id: 'direitos',
    icon: 'ri-shield-user-line',
    title: '7. Direitos do Titular',
    content: [
      'Nos termos da LGPD, você possui os seguintes direitos em relação aos seus dados pessoais:',
    ],
    list: [
      'Confirmação da existência de tratamento dos seus dados',
      'Acesso aos dados pessoais tratados',
      'Correção de dados incompletos, inexatos ou desatualizados',
      'Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos',
      'Portabilidade dos dados a outro fornecedor de serviço',
      'Eliminação dos dados tratados com base no consentimento',
      'Informação sobre compartilhamento de dados com entidades públicas e privadas',
      'Revogação do consentimento a qualquer momento',
    ],
  },
  {
    id: 'seguranca',
    icon: 'ri-lock-2-line',
    title: '8. Segurança dos Dados',
    content: [
      'Adotamos medidas técnicas e organizacionais adequadas para proteger seus dados pessoais contra acesso não autorizado, perda, alteração ou destruição, incluindo:',
    ],
    list: [
      'Comunicação segura via HTTPS com certificado SSL/TLS',
      'Acesso restrito aos dados somente por pessoal autorizado',
      'Uso de plataformas de envio de formulários com proteção de dados',
    ],
  },
  {
    id: 'cookies',
    icon: 'ri-cookie-line',
    title: '9. Cookies',
    content: [
      'Este site pode utilizar cookies de sessão e tecnologias similares para garantir o funcionamento correto das páginas e melhorar a experiência de navegação.',
      'Não utilizamos cookies de rastreamento para fins publicitários. Você pode configurar seu navegador para recusar cookies, mas isso pode afetar a funcionalidade de algumas partes do site.',
    ],
  },
  {
    id: 'alteracoes',
    icon: 'ri-edit-line',
    title: '10. Alterações desta Política',
    content: [
      'Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças na legislação, nas práticas de tratamento de dados ou nos serviços oferecidos.',
      'A data da última atualização está indicada no início deste documento. Recomendamos a consulta periódica desta página.',
    ],
  },
  {
    id: 'contato-dpo',
    icon: 'ri-customer-service-2-line',
    title: '11. Contato e Canal de Comunicação com o DPO',
    content: [
      'Para exercer seus direitos, esclarecer dúvidas ou apresentar reclamações relacionadas ao tratamento dos seus dados pessoais, entre em contato:',
    ],
    list: [
      'E-mail: contato@dralaurafolchini.com.br',
      'WhatsApp: (48) 99100-2443',
    ],
  },
];

// ─── Componente de seção da política ─────────────────────────────────────────

function PolicyBlock({ section, index }: { section: PolicySection; index: number }) {
  return (
    <AnimatedSection delay={index * 60}>
      <div
        id={section.id}
        className="bg-white rounded-2xl border border-[#E8DCC8] p-7 sm:p-9 scroll-mt-24"
      >
        <div className="flex items-start gap-4 mb-5">
          <div className="w-11 h-11 bg-[#FBF8F3] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className={`${section.icon} text-[#C4A57B] text-xl`} />
          </div>
          <h2 className="text-lg sm:text-xl font-medium text-[#4A3F35] leading-snug pt-1.5">
            {section.title}
          </h2>
        </div>

        <div className="pl-0 sm:pl-15 space-y-3">
          {section.content.map((para, i) => (
            <p key={i} className="text-sm sm:text-base text-[#6B5345] leading-relaxed">
              {para}
            </p>
          ))}

          {section.list && (
            <ul className="space-y-2 mt-3">
              {section.list.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-[#6B5345]">
                  <i className="ri-checkbox-circle-line text-[#C4A57B] text-base flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AnimatedSection>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function PrivacidadePage() {
  const [agendamentoOpen, setAgendamentoOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <title>Política de Privacidade | Dra. Laura Folchini Dermatologista</title>
      <meta name="description" content="Política de Privacidade da Dra. Laura Folchini, dermatologista em Criciúma e Tubarão/SC. Tratamento de dados em conformidade com a LGPD (Lei nº 13.709/2018)." />
      <link rel="canonical" href="https://dralaurafolchini.com.br/privacidade" />
      <div className="min-h-screen bg-[#FBF8F3]">
        {agendamentoOpen && <AgendamentoModal onClose={() => setAgendamentoOpen(false)} />}
        <Navbar onAgendar={() => setAgendamentoOpen(true)} />

        {/* Hero */}
        <section className="relative flex items-center pt-16 min-h-[38vh] sm:min-h-[44vh] bg-[#4A3F35] overflow-hidden">
          {/* Padrão decorativo de fundo */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 25% 50%, #C4A57B 1px, transparent 1px), radial-gradient(circle at 75% 50%, #C4A57B 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A3F35]/0 via-transparent to-[#4A3F35]/60" />

          <div className="relative z-10 w-full max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16 py-14 sm:py-20">
            {/* Breadcrumb */}
            <div
              className="mb-7 opacity-0"
              style={{ animation: 'fadeInUp 0.5s ease-out 0.05s forwards' }}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm cursor-pointer group"
              >
                <i className="ri-arrow-left-line text-base group-hover:-translate-x-1 transition-transform" />
                Voltar para Home
              </Link>
            </div>

            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-5 opacity-0"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.15s forwards' }}
              >
                <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
                <span className="text-sm text-white/80 font-medium tracking-wide">LGPD · Lei nº 13.709/2018</span>
              </div>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl 2xl:text-7xl font-light text-white mb-4 leading-tight opacity-0"
                style={{ animation: 'fadeInUp 0.7s ease-out 0.25s forwards' }}
              >
                Política de <span className="italic text-[#E8CFA8]">Privacidade</span>
              </h1>
              <p
                className="text-sm text-white/60 opacity-0"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.38s forwards' }}
              >
                Última atualização: {LAST_UPDATED}
              </p>
            </div>
          </div>
        </section>

        {/* Intro Banner */}
        <section className="bg-[#C4A57B]/10 border-b border-[#C4A57B]/20">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16 py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
              <div className="w-10 h-10 bg-[#C4A57B]/15 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-[#C4A57B] text-xl" />
              </div>
              <p className="text-sm text-[#6B5345] leading-relaxed">
                Prezamos pela sua privacidade e pela transparência no uso dos seus dados. Esta política descreve como coletamos, usamos e protegemos suas informações pessoais em conformidade com a{' '}
                <strong className="text-[#4A3F35]">Lei Geral de Proteção de Dados (LGPD)</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Índice + Conteúdo */}
        <section className="py-12 sm:py-20 bg-[#FBF8F3]">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12 items-start">

              {/* Índice lateral — sticky no desktop */}
              <AnimatedSection direction="left" className="lg:col-span-1">
                <div className="bg-white rounded-2xl border border-[#E8DCC8] p-6 lg:sticky lg:top-24">
                  <p className="text-xs font-semibold text-[#A89580] uppercase tracking-widest mb-4">
                    Índice
                  </p>
                  <nav className="space-y-1.5">
                    {POLICY_SECTIONS.map((s) => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="flex items-center gap-2 text-xs text-[#8B7355] hover:text-[#C4A57B] transition-colors py-1 cursor-pointer leading-snug"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        <i className="ri-arrow-right-s-line text-[#C4A57B] flex-shrink-0" />
                        {s.title.replace(/^\d+\.\s/, '')}
                      </a>
                    ))}
                  </nav>

                  {/* CTA contato dentro do índice */}
                  <div className="mt-6 pt-5 border-t border-[#E8DCC8]">
                    <p className="text-xs text-[#A89580] mb-3 leading-relaxed">
                      Dúvidas sobre privacidade?
                    </p>
                    <a
                      href="mailto:contato@dralaurafolchini.com.br"
                      className="flex items-center gap-2 text-xs font-medium text-[#C4A57B] hover:text-[#B39568] transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-line text-sm" />
                      Enviar e-mail
                    </a>
                  </div>
                </div>
              </AnimatedSection>

              {/* Conteúdo principal */}
              <div className="lg:col-span-3 space-y-5">
                {POLICY_SECTIONS.map((section, index) => (
                  <PolicyBlock key={section.id} section={section} index={index} />
                ))}

                {/* Aviso final */}
                <AnimatedSection delay={POLICY_SECTIONS.length * 60}>
                  <div className="bg-[#4A3F35] rounded-2xl p-7 sm:p-9 text-center">
                    <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-heart-pulse-line text-white text-2xl" />
                    </div>
                    <h3 className="text-xl font-medium text-white mb-2">Sua confiança é nossa prioridade</h3>
                    <p className="text-sm text-white/70 leading-relaxed max-w-xl mx-auto mb-6">
                      Tratamos seus dados com o mesmo cuidado e dedicação que oferecemos no atendimento dermatológico. Em caso de dúvidas, estamos à disposição.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <a
                        href="mailto:contato@dralaurafolchini.com.br"
                        className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-mail-send-line" />
                        Contato sobre privacidade
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-transparent text-white text-sm font-medium rounded-full border-2 border-white/30 hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-home-line" />
                        Voltar para Home
                      </Link>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons onAgendar={() => setAgendamentoOpen(true)} />
      </div>
    </>
  );
}
